import { Injectable } from "@angular/core";
import { DynamicModification } from "../models/dynamic-modification";

@Injectable()
export class DynamicModificationLoaderService {

    /**
    * This method builds a map containing all dynamic modifications placed in dynamic anchor.
     * The key of the map is the AnchorReference of the dynamic anchor and the value is the dynamic modifications placed in that anchor
     * @param dynamicModificationsToSort is a list of all dynamic modifications 
     * @param existingMap is a map of existing dynamic modifications by anchor
     * @returns a map of dynamic anchors and the dynamic modifications placed in them
     */
    sortArrayOfDynamicModificationsByDynamicAnchor(dynamicModificationsToSort: DynamicModification[],
        existingMap: Map<string, DynamicModification[]>): Map<string, DynamicModification[]> {
        let dmInsideDynamicGroupsSortedByAnchorReference = existingMap;
        if (!dmInsideDynamicGroupsSortedByAnchorReference) {
            dmInsideDynamicGroupsSortedByAnchorReference = new Map<string, DynamicModification[]>();
        }
        dynamicModificationsToSort.forEach((dm) => {
            const anchorOfDm: string = dm.anchorReference;
            const isInDynamicgroup = anchorOfDm?.startsWith("eazGroupAnchorRef");
            if (isInDynamicgroup) {
                if (!dmInsideDynamicGroupsSortedByAnchorReference.has(anchorOfDm)) {
                    dmInsideDynamicGroupsSortedByAnchorReference.set(anchorOfDm, []);
                }
                dmInsideDynamicGroupsSortedByAnchorReference.get(anchorOfDm).push(dm);

            }
        });
        return dmInsideDynamicGroupsSortedByAnchorReference;
    }

    /**
     * This method build a map containing all dynamic modifications placed in dynamic anchors.
     * The key of the map is the AnchorReference of the dynamic anchor
     * Then, it deletes all dynamic anchors from the original map.
     * @param dynamicModificationsToSort is the map containing all anchors and the dynamic modification placed in them
     * @returns a map containing all dynamic modifications placed in dynamic anchors.
     */
    extractDynamicModificationsInDynamicAnchor(dynamicModificationsToSort: Map<string, DynamicModification[]>): Map<string, DynamicModification[]> {
        let dmInsideDynamicGroupsSortedByAnchorReference = new Map<string, DynamicModification[]>();
        dynamicModificationsToSort.forEach((value, key) => {
            dmInsideDynamicGroupsSortedByAnchorReference = this.sortArrayOfDynamicModificationsByDynamicAnchor(value, dmInsideDynamicGroupsSortedByAnchorReference);
            if (key.startsWith("eazGroupAnchorRef")) {
                dynamicModificationsToSort.delete(key)
            }
        });
        return dmInsideDynamicGroupsSortedByAnchorReference;
    }

    /**
    * this method extracts a map of dynamic modifications placed in one anchor
    * from the map containing all anchors and the dynamic modifications placed in them
    * @param anchor is the anchor 
    * @param DMs is the map of all anchors and the dynamic modifications placed in them
    * @returns a map of dynamic modifications placed in the anchor
    */
    getDMsByDynamicGroupAnchor(anchor: string, DMs: Map<string, DynamicModification[]>): Map<string, DynamicModification[]> {
        const subMap = new Map<string, any[]>();
        if (DMs.has(anchor)) {
            const value = DMs.get(anchor);
            if (value) {
                subMap.set(anchor, value);
            }
        }
        return subMap;
    }

    public async sortDynamicModificationsByAnchorAndPriorityOrder(
        dynamicModificationsToSort: DynamicModification[]): Promise<Map<string, DynamicModification[]>> {
        return new Promise((resolve) => {
            // Sorting the dynamic modifications by anchor
            const dynamicModifMapSortingDmByAnchor: Map<string, DynamicModification[]>
                = this.sortDynamicModificationsByAnchor(dynamicModificationsToSort);
            // Sorting the dynamic modifications by priority order
            for (const arrayToSortByPriorityOrder of dynamicModifMapSortingDmByAnchor.values()) {
                arrayToSortByPriorityOrder.sort((dm1, dm2) => {
                    if (dm1.priorityOrder < dm2.priorityOrder) {
                        return -1;
                    }
                    if (dm1.priorityOrder > dm2.priorityOrder) {
                        return 1;
                    }
                    return 0;
                });
            }
            resolve(dynamicModifMapSortingDmByAnchor);
        });
    }

    public sortDynamicModificationsByAnchor(dynamicModificationsToSort: DynamicModification[]): Map<string, DynamicModification[]> {
        const dynamicModifMapSortingDmByAnchor: Map<string, DynamicModification[]> = new Map();
        for (const dynModif of dynamicModificationsToSort) {
            let arrayOfDmOfAnAnchor: DynamicModification[];
            if (dynamicModifMapSortingDmByAnchor.has(dynModif.anchorReference)) {
                arrayOfDmOfAnAnchor = dynamicModifMapSortingDmByAnchor.get(dynModif.anchorReference);
            } else {
                arrayOfDmOfAnAnchor = [];
                dynamicModifMapSortingDmByAnchor.set(dynModif.anchorReference, arrayOfDmOfAnAnchor);
            }
            arrayOfDmOfAnAnchor.push(dynModif);
        }
        return dynamicModifMapSortingDmByAnchor;
    }

}
